import { replacePlaceholders, redirect } from "@odin/utils";
import { get } from "lodash-es";
import { useMessages } from "../messages/messages.service.es.js";
import { useI18n } from "../i18n/i18n.es.js";
const { t } = useI18n();
const { postMessage } = useMessages();
let _axios;
let _refs;
let _nextTick;
const FLASH_MESSAGES_HEADER = "x-flash-messages";
const setupActions = (axios, refs, nextTick, WsDialog) => {
  _axios = axios;
  _refs = refs;
  _nextTick = nextTick;
};
const useActions = () => {
  return {
    responseAction,
    actionClicked
  };
};
const responseAction = async (data, headers, opts = {}) => {
  if (opts.processResponseCallback) {
    opts = {
      ...opts,
      ...opts.processResponseCallback(data, headers, { singleAction: true })
    };
  }
  const actionPromise = new Promise((res) => {
    res(executeAction(data, headers, opts));
  });
  return {
    actionPromise,
    killAction: () => {
    }
  };
};
function getPollUrl(data, opts) {
  return (data == null ? void 0 : data.action) === "poll" ? data.url || (opts == null ? void 0 : opts.poll) : opts == null ? void 0 : opts.poll;
}
function getRedirectUrl(data, opts) {
  return (data == null ? void 0 : data.action) === "redirect" ? data.url || (opts == null ? void 0 : opts.redirect) : (data == null ? void 0 : data.redirectTo) || (opts == null ? void 0 : opts.redirect);
}
function handleDialog(opts, dialogAction = "toggle") {
  if (opts == null ? void 0 : opts.openModal) {
    const dialog = _refs[opts.openModal];
    _nextTick(() => {
      dialog ? dialog[dialogAction]() : console.error(`Modal ${opts.openModal} not found`);
    });
  }
}
async function handlePollingDialog(opts, formattedPollUrl, pollLimit) {
  handleDialog(opts, "show");
  if (!formattedPollUrl) {
    handleDialog(opts, "hide");
    return false;
  }
  await pollStatusAfterSubmit(formattedPollUrl, pollLimit, opts);
  handleDialog(opts, "hide");
  return false;
}
const executeAction = async (data, headers, opts) => {
  const contentHeader = headers["content-disposition"];
  const isFileDownload = contentHeader == null ? void 0 : contentHeader.includes("attachment");
  if (isFileDownload) {
    const fileName = contentHeader.replaceAll(/"/g, "").slice(contentHeader.indexOf("=") + 1);
    executeFileDownload(data, headers, fileName);
    return false;
  }
  const replacementData = get(data, "data", data);
  const pollUrl = getPollUrl(data, opts);
  const pollLimit = 30;
  const formattedPollUrl = replacePlaceholders(pollUrl, replacementData);
  const redirectUrl = replacePlaceholders(getRedirectUrl(data, opts), replacementData);
  if (formattedPollUrl) {
    if (opts == null ? void 0 : opts.openModal) {
      return handlePollingDialog(opts, formattedPollUrl, pollLimit);
    } else {
      opts = await pollStatusAfterSubmit(formattedPollUrl, pollLimit, opts) || { redirectUrl };
    }
  }
  let redirectDestination = redirectUrl ?? opts.redirect;
  if (redirectDestination) {
    if (headers[FLASH_MESSAGES_HEADER]) {
      const encodedMessages = encodeURIComponent(headers[FLASH_MESSAGES_HEADER]);
      redirectDestination += `#messages=${encodedMessages}`;
    }
    redirect(redirectDestination);
    return true;
  }
  if (opts == null ? void 0 : opts.reload) {
    window.location.reload();
    return true;
  }
  handleDialog(opts);
  handleFlashMessages(headers, opts.path);
  if (opts == null ? void 0 : opts.reloadComponents) {
    if (headers[FLASH_MESSAGES_HEADER]) {
      const allMessages = JSON.parse(headers[FLASH_MESSAGES_HEADER]);
      for (const message of allMessages) {
        postMessage("general", { value: message.text, payload: message });
      }
    }
    reloadComponents(opts.reloadComponents);
    return false;
  }
  return false;
};
function handleFlashMessages(headers, path) {
  var _a;
  if ((_a = headers[FLASH_MESSAGES_HEADER]) == null ? void 0 : _a.length) {
    const messages = JSON.parse(headers[FLASH_MESSAGES_HEADER]);
    messages.forEach((messagePayload) => {
      const message = {
        value: messagePayload.text || messagePayload.title || "Error",
        payload: messagePayload
      };
      postMessage(path || `title.${messagePayload.type}` || "general", message);
    });
  }
}
function reloadComponents(componentsToReload) {
  componentsToReload.forEach((cmp) => {
    var _a;
    (_a = _refs[cmp]) == null ? void 0 : _a.reload();
  });
}
const pollStatusAfterSubmit = (url, limit, opts) => (
  // eslint-disable-next-line sonarjs/cognitive-complexity
  new Promise(async (res, rej) => {
    if (limit <= 0) {
      return rej(t("uikit:actions_service.polling_limit_reached"));
    }
    try {
      const { data, headers, status } = await _axios({ url });
      if (status === 200) {
        if (opts.processResponseCallback) {
          return res(opts.processResponseCallback(data, headers, { pollingAction: true }));
        } else {
          if (data.redirectTo) {
            return res({
              ...opts,
              redirect: data.redirectTo
            });
          } else {
            return res(opts);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
    setTimeout(async () => {
      try {
        res(await pollStatusAfterSubmit(url, limit - 1, opts));
      } catch (err) {
        rej(err);
      }
    }, 2e3);
  })
);
const executeFileDownload = (data, headers, fileName) => {
  const blob = new Blob([data], { type: headers["content-type"] });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};
const actionClicked = async (action, context, actionUrl, event) => {
  context == null ? void 0 : context.$emit("action-clicked", action);
  if (context == null ? void 0 : context.isDisabled) {
    context == null ? void 0 : context.disabledBulkActionData.disabledBulkActionHandler(action.name, action.disableCondition);
    return;
  }
  if (context == null ? void 0 : context.onSuccess) {
    action.onSuccess = context.onSuccess;
  }
  if (action.ajax) {
    context == null ? void 0 : context.$emit("ajax-action-call", action);
  } else if (typeof action.clicked === "function") {
    action.clicked(action);
  } else if (action.modalRef && (context == null ? void 0 : context.$root.$refs[action.modalRef])) {
    await handleDialogAction(action, context);
  } else if (action.url || action.urlExpression) {
    handleRedirectAction(action, actionUrl, event);
  }
  return false;
};
const handleRedirectAction = (action, actionUrl, event) => {
  var _a;
  if (((_a = action == null ? void 0 : action.attributes) == null ? void 0 : _a.target) === "_blank" || event instanceof MouseEvent && ((event == null ? void 0 : event.metaKey) || (event == null ? void 0 : event.ctrlKey))) {
    window.open(actionUrl || action.url, "_blank");
  } else {
    redirect(actionUrl || action.url);
  }
};
const handleDialogAction = async (action, context) => {
  if (action.rowData) {
    (context == null ? void 0 : context.$root).$wData.set("modalData", {
      action,
      rowData: action.rowData
    });
  }
  const cta = action;
  const formData = action.rowData;
  await (context == null ? void 0 : context.$nextTick());
  let wsDialog = context == null ? void 0 : context.$root.$refs[action.modalRef];
  if (wsDialog && action.refIndex !== void 0) {
    wsDialog = get(wsDialog, action.refIndex, wsDialog);
  }
  if (wsDialog) {
    wsDialog.setModalData(action.rowData);
    setTimeout(() => {
      wsDialog.toggle();
    }, 0);
  }
  if (context == null ? void 0 : context.emitInActions) {
    await (context == null ? void 0 : context.$nextTick());
    context == null ? void 0 : context.emitInActions("show-cta-modal", action.modalRef, { cta, rowData: formData });
  }
};
export {
  actionClicked,
  responseAction,
  setupActions,
  useActions
};
//# sourceMappingURL=actions.service.es.js.map
